<template>
    <kiosk-cloning>
        <div class="flex flex-col w-full">
            <page-header> Clone Teams </page-header>

            <div class="max-w-(--breakpoint-sm) flex flex-col gap-4">
                <kiosk-clone-teams-source-team v-model:form="form" />
                <kiosk-clone-teams-cloning-targets v-model:form="form" />
                <kiosk-clone-teams-target-teams v-model:form="form" />

                <kiosk-clone-teams-clonable-resources v-model:form="form" :limit="50" resourceType="segments" />

                <kiosk-clone-teams-clonable-resources v-model:form="form" :limit="50" resourceType="campaigns" />

                <kiosk-clone-teams-clonable-resources v-model:form="form" :limit="50" resourceType="automations" />
                <kiosk-clone-teams-clonable-resources
                    v-model:form="form"
                    :limit="50"
                    resourceType="team_custom_attributes"
                />
                <kiosk-clone-teams-clonable-resources v-model:form="form" :limit="50" resourceType="snippets" />

                <vx-sticky-footer>
                    <vx-button
                        @click="submit()"
                        :loading="submitting"
                        data-test="submit-button"
                        :disabled="submitDisabled"
                        class="grow"
                        size="lg"
                        color="primary"
                    >
                        Clone
                    </vx-button>
                </vx-sticky-footer>
            </div>
        </div>
    </kiosk-cloning>
</template>

<script setup>
import KioskCloning from '../KioskCloning.vue';
import KioskCloneTeamsSourceTeam from './KioskCloneTeamsSourceTeam.vue';
import KioskCloneTeamsCloningTargets from './KioskCloneTeamsCloningTargets.vue';
import KioskCloneTeamsTargetTeams from './KioskCloneTeamsTargetTeams.vue';
import KioskCloneTeamsClonableResources from './KioskCloneTeamsClonableResources.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { VxButton, VxStickyFooter, VxSwal } from '@voxie/frontend-components';
import { ref, computed } from 'vue';
import { adminCloningClient } from '~/services';
import useVuelidate from '@vuelidate/core';
import { validateRules } from '~/utils/validation';
import { throwSwalRequestError } from '~/utils/request';

const submitting = ref(false);
const defaultForm = () => {
    return {
        source_team: null,
        cloning_targets: [],
        target_teams: [],
        segments: [],
        campaigns: [],
        automations: [],
        snippets: [],
        team_custom_attributes: [],
    };
};
const form = ref(defaultForm());

const submitDisabled = computed(() => {
    return (
        !(form.value.source_team?.id && form.value.target_teams.length) ||
        !(
            form.value.segments.length ||
            form.value.campaigns.length ||
            form.value.automations.length ||
            form.value.snippets.length ||
            form.value.team_custom_attributes.length
        )
    );
});

const requestCloneTeamResources = async () => {
    let data = {
        source_team_id: form.value.source_team.id,
        target_team_ids: form.value.target_teams.map((team) => team.id),
    };

    const resources = {
        source_automation_rule_ids: form.value.automations.map((s) => s.id),
        source_campaign_ids: form.value.campaigns.map((s) => s.id),
        source_segment_ids: form.value.segments.map((s) => s.id),
        source_snippet_ids: form.value.snippets.map((s) => s.id),
        source_team_custom_attribute_ids: form.value.team_custom_attributes.map((s) => s.id),
    };

    Object.keys(resources).forEach((resource) => {
        if (resources[resource].length) {
            data[resource] = resources[resource];
        }
    });

    return adminCloningClient.cloneResources(data);
};

const validate = useVuelidate();

const submit = async () => {
    if (submitDisabled.value) {
        return;
    }

    if (!(await validateRules(validate))) {
        return;
    }

    const targetTeamNames = form.value.target_teams.map((team) => team.name);
    const text = `You're going to clone team ${form.value.source_team.name} into team ${targetTeamNames}.`;

    VxSwal.fire({
        title: 'Are you sure?',
        text: text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, clone it!',
        preConfirm: () => {
            submitting.value = true;

            return requestCloneTeamResources()
                .catch(throwSwalRequestError)
                .finally(() => {
                    submitting.value = false;
                });
        },
    }).then(({ value: isActionConfirmed }) => {
        if (!isActionConfirmed) {
            return;
        }

        form.value = defaultForm();
        validate.value.$reset();

        VxSwal.fire({
            title: 'Success',
            text: 'Your cloning request has been received.',
            icon: 'success',
        });
    });
};
</script>
